/**
 * useIntegrationConnections — Integration Hub (Fase 1)
 * -----------------------------------------------------------------------------
 * Hook único de acesso às conexões de integração do tenant.
 *
 * Regras de arquitetura:
 *   - 100% via RPC SECURITY DEFINER (nada de SELECT direto em tabelas).
 *   - Credenciais NUNCA são lidas de volta: o segredo só trafega no envio.
 *   - Toda chamada ao sistema externo passa pela edge function
 *     `integration-gateway` (nenhum provider é chamado do navegador).
 *
 * @version 1.4.0
 * RELEASE: 2026-08-23 v1.4.0 - Blindagem contra vazamento de segredo: erros do
 *          Postgres nunca são propagados com `details`/`hint` (que contêm a
 *          linha inteira, incluindo a chave de API) e nenhum payload sensível
 *          vai para log ou toast.
 * RELEASE: 2026-08-23 v1.3.0 - Documentação do conector: overview_md,
 *          setup_guide_md, support_url e help_md por recurso.
 * RELEASE: 2026-08-23 v1.2.0 - Recursos por conexão: o usuário escolhe quais
 *          operações do provider ficam ativas em cada conexão.
 * RELEASE: 2026-08-23 v1.1.0 - Catálogo por categoria (category/icon/docs_url) e
 *          suporte a múltiplas conexões por provider com conexão padrão.
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Integration Hub Fase 1).
 */




import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('IntegrationConnections');

// =============================================================================
// SEGURANÇA: SANITIZAÇÃO DE ERROS E PAYLOADS
// =============================================================================

/** Campos que jamais podem aparecer em log, toast ou telemetria */
const SENSITIVE_KEYS = new Set([
  'key_value',
  'token',
  'password',
  'secret',
  'api_key',
  'apikey',
  'fields',
  'credentials',
]);

/**
 * Remove valores sensíveis de qualquer objeto antes de log/telemetria.
 * Mantém apenas a indicação de que o campo foi preenchido.
 */
export function sanitizeSensitivePayload(input: unknown): unknown {
  if (!input || typeof input !== 'object') return input;
  if (Array.isArray(input)) return input.map(sanitizeSensitivePayload);

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (SENSITIVE_KEYS.has(key.toLowerCase())) {
      out[key] = value ? '[REDACTED]' : null;
    } else {
      out[key] = sanitizeSensitivePayload(value);
    }
  }
  return out;
}

/**
 * Converte um erro do PostgREST/Supabase em mensagem segura para o usuário.
 * O `details`/`hint` do Postgres pode conter a LINHA INTEIRA que falhou —
 * incluindo a credencial em texto puro — portanto nunca é exibido nem logado.
 */
function safeErrorMessage(error: unknown, fallback: string): string {
  const err = error as { code?: string; message?: string } | null;
  const code = err?.code ?? '';

  // Erros estruturais do banco não devem virar texto para o usuário
  if (/^(22|23|42|53|55|XX)/.test(code)) return fallback;

  const message = (err?.message ?? '').trim();
  if (!message) return fallback;

  // Descarta mensagens que possam carregar conteúdo de linha/credencial
  if (/failing row|duplicate key|violates|DETAIL:|\{/i.test(message)) return fallback;

  return message.slice(0, 200);
}

/** Log seguro: apenas código e mensagem sanitizada */
function logSafeError(context: string, error: unknown, fallback: string) {
  const err = error as { code?: string } | null;
  logger.error(context, {
    code: err?.code ?? 'unknown',
    message: safeErrorMessage(error, fallback),
  });
}


// =============================================================================
// TIPOS
// =============================================================================

/** Operação exposta por um provider */
export interface IntegrationOperation {
  code: string;
  name: string;
  description: string | null;
  /** Ajuda contextual do recurso em markdown (o que faz / onde aparece) */
  help_md?: string | null;
  supports_pagination: boolean;
  is_importable: boolean;
  is_live_query: boolean;
  /** Esquema dos parâmetros da operação (usado no modal "Nova consulta") */
  params_schema?: unknown;

}


/** Estado de um recurso (operação) dentro de uma conexão específica */
export interface ConnectionOperationState {
  code: string;
  name: string;
  description: string | null;
  is_importable: boolean;
  is_live_query: boolean;
  /** Recurso ativado pelo usuário nesta conexão */
  is_enabled: boolean;
}

/** Provider disponível no catálogo global */
export interface IntegrationProvider {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  auth_type: string;
  /** Categoria do catálogo (erp, tracking, financial, signature, generic, outros) */
  category: string;
  icon: string | null;
  docs_url: string | null;
  /** Link de suporte do aplicativo */
  support_url?: string | null;
  /** Documentação em markdown: visão geral e passo a passo de configuração */
  overview_md?: string | null;
  setup_guide_md?: string | null;
  /**
   * Provider com integração nativa: os parâmetros técnicos de autenticação
   * (nome do parâmetro / local de envio) são fixos e definidos no catálogo,
   * portanto não são exibidos nem editáveis no formulário de conexão.
   * A URL base continua editável (varia de cliente para cliente).
   */
  is_managed?: boolean;

  /**
   * Meio de comunicação do aplicativo:
   *   - 'http'  : consulta/importação por API (padrão)
   *   - 'mysql' : envio direto para um banco de dados do cliente (só saída)
   */
  transport?: string | null;

  config_schema: Record<string, unknown> | null;
  operations: IntegrationOperation[];
}

/** Conexão configurada pelo tenant (sem segredo) */
export interface IntegrationConnection {
  id: string;
  name: string;
  provider_id: string;
  provider_slug: string;
  provider_name: string;
  base_url: string | null;
  config: Record<string, unknown> | null;
  credential_id: string | null;
  credential_name: string | null;
  status: string;
  is_active: boolean;
  /** Conexão padrão do provider dentro do tenant (uma por provider) */
  is_default: boolean;
  last_test_at: string | null;

  last_success_at: string | null;
  last_error: string | null;
  operations_count: number;
  /** Quantidade de recursos habilitados pelo usuário nesta conexão */
  enabled_operations_count: number;
  /** Recursos do provider com o estado (ligado/desligado) nesta conexão */
  enabled_operations: ConnectionOperationState[];
  created_at: string;
}

/** Payload de gravação de conexão (credencial opcional) */
export interface SaveConnectionInput {
  connectionId?: string | null;
  name: string;
  providerSlug: string;
  baseUrl?: string | null;
  config?: Record<string, unknown>;
  isActive?: boolean;
  /** Segredo em claro — enviado apenas quando o usuário preenche o formulário */
  credentials?: Record<string, unknown> | null;
  credentialId?: string | null;
  /**
   * Códigos dos recursos que o usuário deixou ativos na conexão.
   * `undefined`/`null` mantém o comportamento antigo (todos ativos).
   */
  enabledOperationCodes?: string[] | null;
}

interface UseIntegrationConnectionsOptions {
  search?: string;
  providerSlug?: string | null;
}

// =============================================================================
// HOOK
// =============================================================================

export function useIntegrationConnections(options: UseIntegrationConnectionsOptions = {}) {
  const { search = '', providerSlug = null } = options;
  const queryClient = useQueryClient();

  /** Catálogo global de providers (raramente muda → cache longo) */
  const providersQuery = useQuery({
    queryKey: ['integration-providers'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<IntegrationProvider[]> => {
      const { data, error } = await supabase.rpc('list_integration_providers');
      if (error) throw error;
      return (data ?? []) as unknown as IntegrationProvider[];
    },
  });

  /** Conexões do tenant ativo */
  const connectionsQuery = useQuery({
    queryKey: ['integration-connections', providerSlug, search],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<IntegrationConnection[]> => {
      const { data, error } = await supabase.rpc('list_integration_connections', {
        p_provider_slug: providerSlug,
        p_search: search.trim() || null,
      });
      if (error) throw error;
      return (data ?? []) as unknown as IntegrationConnection[];
    },
  });

  /** Invalida todas as listagens de conexões */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['integration-connections'] });
  };

  /**
   * Cria/atualiza conexão. Quando o usuário informa credenciais, elas são
   * gravadas primeiro (RPC dedicada) e o id resultante é vinculado à conexão.
   */
  const saveConnection = useMutation({
    mutationFn: async (input: SaveConnectionInput): Promise<string> => {
      let credentialId = input.credentialId ?? null;

      if (input.credentials && Object.keys(input.credentials).length > 0) {
        const { data: credData, error: credError } = await supabase.rpc(
          'upsert_integration_credential',
          {
            p_name: input.name,
            p_provider_slug: input.providerSlug,
            p_credentials: input.credentials as never,
            p_credential_id: credentialId,
          },
        );
        // Nunca propagamos o erro cru: ele pode conter a linha (com o segredo)
        if (credError) {
          logSafeError(
            'Falha ao salvar credencial da conexão',
            credError,
            'Não foi possível salvar a credencial da conexão',
          );
          throw new Error(
            safeErrorMessage(credError, 'Não foi possível salvar a credencial da conexão'),
          );
        }
        credentialId = credData as string;
      }

      const { data, error } = await supabase.rpc('upsert_integration_connection', {
        p_name: input.name,
        p_provider_slug: input.providerSlug,
        p_base_url: input.baseUrl ?? null,
        p_config: (input.config ?? {}) as never,
        p_credential_id: credentialId,
        p_is_active: input.isActive ?? true,
        p_connection_id: input.connectionId ?? null,
        // Recursos escolhidos pelo usuário (null = todos ativos)
        p_enabled_operation_codes: input.enabledOperationCodes ?? null,
      });
      if (error) {
        logSafeError('Falha ao salvar conexão', error, 'Não foi possível salvar a conexão');
        throw new Error(safeErrorMessage(error, 'Não foi possível salvar a conexão'));
      }
      return data as string;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Conexão salva com sucesso');
    },
    onError: (error: Error) => {
      toast.error(safeErrorMessage(error, 'Não foi possível salvar a conexão'));
    },
  });


  /** Exclui uma conexão do tenant */
  const deleteConnection = useMutation({
    mutationFn: async (connectionId: string) => {
      const { error } = await supabase.rpc('delete_integration_connection', {
        p_connection_id: connectionId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Conexão excluída');
    },
    onError: (error: Error) => {
      logSafeError('Falha ao excluir conexão', error, 'Não foi possível excluir a conexão');
      toast.error(safeErrorMessage(error, 'Não foi possível excluir a conexão'));
    },
  });

  /** Testa a conexão no provider através do gateway */
  const testConnection = useMutation({
    mutationFn: async (connectionId: string): Promise<{ ok: boolean; message?: string }> => {
      const { data, error } = await supabase.functions.invoke('integration-gateway', {
        body: { action: 'test_connection', connection_id: connectionId },
      });
      if (error) throw error;
      return data as { ok: boolean; message?: string };
    },
    onSuccess: (result) => {
      invalidate();
      if (result?.ok) toast.success(result.message || 'Conexão testada com sucesso');
      else toast.error(result?.message || 'Falha ao conectar com o sistema externo');
    },
    onError: (error: Error) => {
      invalidate();
      logSafeError('Falha no teste de conexão', error, 'Não foi possível testar a conexão');
      toast.error(safeErrorMessage(error, 'Não foi possível testar a conexão'));
    },
  });

  /**
   * Define a conexão padrão do provider. A RPC desmarca as demais do mesmo
   * provider de forma atômica (índice único parcial garante a consistência).
   */
  const setDefaultConnection = useMutation({
    mutationFn: async (connectionId: string) => {
      const { error } = await supabase.rpc('set_default_integration_connection', {
        p_connection_id: connectionId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Conexão definida como padrão');
    },
    onError: (error: Error) => {
      logSafeError('Falha ao definir conexão padrão', error, 'Não foi possível definir a conexão padrão');
      toast.error(safeErrorMessage(error, 'Não foi possível definir a conexão padrão'));
    },
  });


  /**
   * Liga/desliga um recurso (operação) de uma conexão específica.
   * A validação real acontece no backend (RPC + gateway).
   */
  const setConnectionOperation = useMutation({
    mutationFn: async (input: {
      connectionId: string;
      operationCode: string;
      enabled: boolean;
    }) => {
      const { error } = await supabase.rpc('set_connection_operation_enabled', {
        p_connection_id: input.connectionId,
        p_operation_code: input.operationCode,
        p_enabled: input.enabled,
      });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.enabled ? 'Recurso ativado' : 'Recurso desativado');
    },
    onError: (error: Error) => {
      logSafeError('Falha ao alterar recurso da conexão', error, 'Não foi possível alterar o recurso');
      toast.error(safeErrorMessage(error, 'Não foi possível alterar o recurso'));
    },
  });

  return {
    providers: providersQuery.data ?? [],
    isLoadingProviders: providersQuery.isLoading,
    connections: connectionsQuery.data ?? [],
    isLoading: connectionsQuery.isLoading,
    isFetching: connectionsQuery.isFetching,
    refetch: connectionsQuery.refetch,
    saveConnection,
    deleteConnection,
    testConnection,
    setDefaultConnection,
    setConnectionOperation,
  };
}

// =============================================================================
// HOOKS AUXILIARES (consumo por outros módulos)
// =============================================================================

/**
 * Retorna as conexões ATIVAS de um provider e a conexão padrão resolvida.
 *
 * Usado por módulos consumidores (ex.: Conta Corrente escolhendo qual conexão
 * Asaas usar). Quando o módulo não expõe escolha ao usuário, basta consumir
 * `defaultConnection`.
 */
export function useDefaultIntegrationConnection(providerSlug: string | null) {
  const { connections, isLoading } = useIntegrationConnections({
    providerSlug: providerSlug ?? null,
  });

  const activeConnections = connections.filter((c) => c.is_active);
  const defaultConnection =
    activeConnections.find((c) => c.is_default) ?? activeConnections[0] ?? null;

  return { connections: activeConnections, defaultConnection, isLoading };
}


/**
 * Verifica se o tenant possui alguma conexão ATIVA com o recurso (operação)
 * habilitado pelo usuário. Usado por telas/nodes integrados pela equipe técnica
 * para decidir se o recurso deve ser oferecido.
 *
 * A checagem também é feita no backend (gateway) — aqui é apenas usabilidade.
 */
export function useConnectionCapability(operationCode: string, providerSlug: string | null = null) {
  const query = useQuery({
    queryKey: ['integration-connections', 'capability', providerSlug, operationCode],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IntegrationConnection[]> => {
      const { data, error } = await supabase.rpc('list_integration_connections', {
        p_provider_slug: providerSlug,
        p_search: null,
        p_operation_code: operationCode,
      });
      if (error) throw error;
      return (data ?? []) as unknown as IntegrationConnection[];
    },
  });

  const connections = (query.data ?? []).filter((c) => c.is_active);

  return {
    connections,
    defaultConnection: connections.find((c) => c.is_default) ?? connections[0] ?? null,
    isEnabled: connections.length > 0,
    isLoading: query.isLoading,
  };
}
